// src/components/QuestionDetailModal.jsx
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import './QuestionDetailModal.css';

// Helper function to generate choice letters (A, B, C, D)
const getChoiceLetter = (index) => String.fromCharCode(65 + index); // 65 is ASCII for 'A'

// Helper: convert backtick-wrapped math → dollar-wrapped math
const preprocessMath = (text = '') =>
  text
    .replace(/`([^`]+)`/g, (_match, p1) => `$${p1}$`)
    .replace(/```(?:math)?\s*([\s\S]*?)```/g, (_match, p1) => `$$${p1}$$`);

const QuestionDetailModal = ({
  question,
  totalQuestions,
  currentQuestionIndex,
  onClose,
  onNext,
  onPrev,
}) => {
  if (!question) return null;

  const {
    question: questionText,
    choices,
    correct_answer: correctAnswerLetter,
    selected_answer: userAnswerLetter,
    explanations,
  } = question;

  const isCorrect = question.is_correct;
  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          &times;
        </button>
        <div className="modal-header">
          <h3>
            Question {currentQuestionIndex + 1} of {totalQuestions}
          </h3>
          <p className={`question-status ${isCorrect ? 'correct' : 'incorrect'}`}>
            {isCorrect ? 'Correct!' : 'Incorrect'}
          </p>
        </div>

        <div className="question-body">
          {/* Render question text with KaTeX support */}
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex]}
            className="question-text"
          >
            {preprocessMath(questionText)}
          </ReactMarkdown>

          <div className="choices">
            {choices &&
              choices.map((choiceText, index) => {
                const choiceLetter = getChoiceLetter(index);
                const isCorrectChoice = choiceLetter === correctAnswerLetter;
                const isUserChoice = choiceLetter === userAnswerLetter;

                return (
                  <div
                    key={index}
                    className={`choice-item
                      ${isCorrectChoice ? 'correct-answer' : ''}
                      ${isUserChoice && !isCorrectChoice ? 'user-incorrect-answer' : ''}
                      ${isUserChoice && isCorrectChoice ? 'user-correct-answer' : ''}
                    `}
                  >
                    <strong>{choiceLetter}.</strong>{' '}
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkMath]}
                      rehypePlugins={[rehypeKatex]}
                    >
                      {preprocessMath(choiceText)}
                    </ReactMarkdown>
                  </div>
                );
              })}
          </div>

          <div className="explanation-section">
            <h4>Explanation:</h4>
            {explanations && explanations[correctAnswerLetter] && (
              <div className="explanation-block correct-explanation">
                <h5>Correct Answer ({correctAnswerLetter}):</h5>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkMath]}
                  rehypePlugins={[rehypeKatex]}
                >
                  {preprocessMath(explanations[correctAnswerLetter])}
                </ReactMarkdown>
              </div>
            )}

            {userAnswerLetter &&
              !isCorrect &&
              explanations &&
              explanations[userAnswerLetter] && (
                <div className="explanation-block user-incorrect-explanation">
                  <h5>Your Answer ({userAnswerLetter}):</h5>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                  >
                    {preprocessMath(explanations[userAnswerLetter])}
                  </ReactMarkdown>
                </div>
              )}

            {(!explanations ||
              (!explanations[correctAnswerLetter] &&
                (!userAnswerLetter || !explanations[userAnswerLetter]))) && (
              <p className="no-explanation-message">
                No detailed explanation available for this question.
              </p>
            )}
          </div>
        </div>

        <div className="modal-navigation">
          <button onClick={onPrev} disabled={isFirstQuestion} className="nav-button">
            &larr; Previous
          </button>
          <button onClick={onNext} disabled={isLastQuestion} className="nav-button">
            Next &rarr;
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionDetailModal;
